import { getEnv } from "@/config/env";
import { PromptLibrary, PromptLibraryUsage, TemplatePromptLibrary } from "@/types/models";

export interface TemplateTypeItem {
  id: number;
  name: string;
  is_active: boolean;
  settings: string | null;
}

export interface TemplateFormatTypeItem {
  id: number;
  name: string;
  is_active: boolean;
}

const PROMPT_LIBRARY_API_URL = getEnv("VITE_PROMPT_LIBRARY_API_URL") || "http://localhost:8005";

let getTokenFn: (() => Promise<string | null>) | null = null;

export function setPromptLibraryTokenGetter(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

async function promptLibraryApiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getTokenFn ? await getTokenFn() : null;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(`${PROMPT_LIBRARY_API_URL}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `API error: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

const api = {
  get: <T>(path: string) => promptLibraryApiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    promptLibraryApiFetch<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    promptLibraryApiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => promptLibraryApiFetch<T>(path, { method: "DELETE" }),
};

export const promptLibraryApi = {
  // Prompt Library operations
  getPromptLibraries: (): Promise<PromptLibrary[]> => {
    return api.get("/api/prompts/prompt-libraries");
  },

  getPromptLibrariesByTemplate: (templateId: string): Promise<PromptLibrary[]> => {
    return api.get(`/api/prompts/prompt-libraries?template_id=${templateId}`);
  },

  getPromptLibrary: (id: string): Promise<PromptLibrary> =>
    api.get(`/api/prompts/prompt-libraries/${id}`),

  createPromptLibrary: (data: Partial<PromptLibrary>): Promise<PromptLibrary> =>
    api.post("/api/prompts/prompt-libraries", data),

  updatePromptLibrary: (id: string, data: Partial<PromptLibrary>): Promise<PromptLibrary> =>
    api.put(`/api/prompts/prompt-libraries/${id}`, data),

  deletePromptLibrary: (id: string): Promise<void> =>
    api.delete(`/api/prompts/prompt-libraries/${id}`),

  getPromptLibraryUsage: (): Promise<PromptLibraryUsage[]> =>
    api.get("/api/prompts/prompt-libraries/usage"),

  // Template-PromptLibrary Association operations
  getAssociations: (params?: { templateId?: string; promptLibraryId?: string }): Promise<TemplatePromptLibrary[]> => {
    const queryParams = new URLSearchParams();
    if (params?.templateId) queryParams.append("template_id", params.templateId);
    if (params?.promptLibraryId) queryParams.append("prompt_library_id", params.promptLibraryId);
    const queryString = queryParams.toString();
    const path = queryString ? `/api/prompts/templates-promptlibraries?${queryString}` : "/api/prompts/templates-promptlibraries";
    return api.get(path);
  },

  getAssociation: (id: string): Promise<TemplatePromptLibrary> =>
    api.get(`/api/prompts/templates-promptlibraries/${id}`),

  createAssociation: (data: { id: string; template_id: string; prompt_library_id: string; ordinal?: number | null }): Promise<TemplatePromptLibrary> =>
    api.post("/api/prompts/templates-promptlibraries", data),

  updateAssociation: (id: string, data: { ordinal?: number | null }): Promise<TemplatePromptLibrary> =>
    api.put(`/api/prompts/templates-promptlibraries/${id}`, data),

  deleteAssociation: (id: string): Promise<void> =>
    api.delete(`/api/prompts/templates-promptlibraries/${id}`),

  // Template Type operations
  getAdminTemplateTypes: (): Promise<TemplateTypeItem[]> =>
    api.get("/api/prompts/template-types"),

  createTemplateType: (data: { name: string; is_active?: boolean; settings?: string | null }): Promise<TemplateTypeItem> =>
    api.post("/api/prompts/template-types", data),

  updateTemplateType: (id: number, data: Partial<{ name: string; is_active: boolean; settings: string | null }>): Promise<TemplateTypeItem> =>
    api.put(`/api/prompts/template-types/${id}`, data),

  deleteTemplateType: (id: number): Promise<void> =>
    api.delete(`/api/prompts/template-types/${id}`),

  // Template Format Type operations
  getAdminTemplateFormatTypes: (): Promise<TemplateFormatTypeItem[]> =>
    api.get("/api/prompts/template-format-types"),

  createTemplateFormatType: (name: string): Promise<TemplateFormatTypeItem> =>
    api.post("/api/prompts/template-format-types", { name }),

  updateTemplateFormatType: (id: number, data: Partial<{ name: string; is_active: boolean }>): Promise<TemplateFormatTypeItem> =>
    api.put(`/api/prompts/template-format-types/${id}`, data),

  deleteTemplateFormatType: (id: number): Promise<void> =>
    api.delete(`/api/prompts/template-format-types/${id}`),
};
